import classNames from "classnames";
import * as React from "react";

import {
  ACTIVITY_WINDOW_MAX_LABEL,
  type ActivityPeriod,
  type InvocationGroupBy,
  type InvocationStatus,
  type PeriodOption,
  type TimeRange,
  type TimeWindow,
  currentMinute,
  periodOptionsFor,
  widthOf,
} from "@/shared/domain/actions";
import { ChevronRight, Info } from "@/shared/foundation/icons";
import { Spinner, Toggle, ToggleGroup } from "@/shared/foundation/primitives";
import type { Condition } from "@/types";

import { ActivityChartFooter } from "./ActivityChartFooter";
import {
  ACTIVITY_CHART_MODES,
  type ActivityChartMode,
} from "./activityChartModes";
import {
  ACTIVITY_LOAD_ERROR,
  ActivityFigureCards,
  ActivityFigureSummary,
} from "./ActivityFigures";
import {
  AUTO_REFRESH_INTERVAL_MS,
  AutoRefreshButton,
} from "./AutoRefreshButton";
import styles from "./InvocationActivity.module.css";
import { InvocationActivityChart } from "./InvocationActivityChart";
import { useActivityPanelData } from "./useActivityPanelData";
import { WaitingBreakdown } from "./WaitingBreakdown";

export interface InvocationActivityPanelProps {
  /** Everything the page is filtered by except status, in the query API's vocabulary. */
  condition?: Condition;
  /** Internal statuses the page is filtered to. Applied to what is drawn, not to what is fetched. */
  statusFilter: InvocationStatus[];
  /** The span drawn. Its key decides when the panel fetches afresh, its bounds what it asks for. */
  window: TimeWindow;
  /** Called when the reader drags a narrower span on the chart. */
  onWindowChange: (window: TimeRange) => void;
  defaultExpanded: boolean;
  /** Namespace for the remembered expanded state; one per place the panel appears. */
  storageKey: string;
  autoRefresh: boolean;
  onAutoRefreshChange: (on: boolean) => void;
  /**
   * Puts the page back to its defaults: filters, date range and anything the panel itself remembers
   * (chart reading, bucket, hidden series). Rendered as a "Reset view" button when given.
   */
  onReset?: () => void;
}

/**
 * What the listed invocations have been doing: a chart of the window, the figures a reader checks
 * first, and what is waiting right now. Collapsible, so the table can have the page; collapsed, the
 * figures stay on the heading line, so the queries run either way.
 */
export function InvocationActivityPanel({
  condition,
  statusFilter,
  // Bound to a different name: `window` would shadow the DOM global for the whole body.
  window: timeWindow,
  onWindowChange,
  defaultExpanded,
  storageKey,
  autoRefresh,
  onAutoRefreshChange,
  onReset,
}: InvocationActivityPanelProps) {
  const expandedKey = `${storageKey}.expanded`;
  const [expanded, setExpanded] = React.useState<boolean>(
    () => readStoredBoolean(expandedKey) ?? defaultExpanded,
  );
  const [mode, setMode] = React.useState<ActivityChartMode>("rollup");
  const [groupBy, setGroupBy] = React.useState<InvocationGroupBy[]>(["action"]);
  // "auto" is the finest the window allows.
  const [chosenPeriod, setChosenPeriod] = React.useState<
    ActivityPeriod | "auto"
  >("auto");
  const bodyId = React.useId();
  // Remounts the chart, which is where the legend's hidden series live.
  const [chartInstance, setChartInstance] = React.useState(0);

  const resetView = () => {
    setMode("rollup");
    setGroupBy(["action"]);
    setChosenPeriod("auto");
    setChartInstance((current) => current + 1);
    onReset?.();
  };

  // The queries resolve the window themselves; this copy is for the picker and the notice. A
  // chosen width is checked against an unfitted window's bounds here. A fitted window's bounds
  // come back with the response, so `fetchFittedActivity` checks the width against those instead.
  const resolved = timeWindow.resolve(currentMinute());
  const fitted = timeWindow.fitToActivity === true;
  const requestable = allowedPeriodsOf(periodOptionsFor(widthOf(resolved)));
  const period =
    chosenPeriod !== "auto" && (fitted || requestable.includes(chosenPeriod))
      ? chosenPeriod
      : undefined;

  const {
    activity,
    queueStats,
    waitingGroups,
    buckets,
    figures,
    isPending,
    isError,
    isPlaceholder,
  } = useActivityPanelData({
    window: timeWindow,
    period,
    condition,
    groupBy,
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL_MS : false,
  });

  // The window on screen. A fitted window's is the response in hand (the previous one while the
  // next loads), drawable no finer than its own bucket.
  const drawn =
    fitted && activity.data !== undefined
      ? {
          start: new Date(activity.data.start_time),
          end: new Date(activity.data.end_time),
          finest: activity.data.period,
        }
      : { ...resolved, finest: undefined };
  const periodOptions = periodOptionsFor(widthOf(drawn), drawn.finest);
  const allowedPeriods = allowedPeriodsOf(periodOptions);
  const chosenDrawable =
    chosenPeriod === "auto" || allowedPeriods.includes(chosenPeriod);
  // A chosen width the window on screen cannot be drawn at is reset, so it does not come back on
  // its own when a later window allows it again.
  React.useEffect(() => {
    if (!chosenDrawable) {
      setChosenPeriod("auto");
    }
  }, [chosenDrawable]);

  // The condition is everything the page narrows the figures by; the status filter reaches only
  // what is drawn. So this is exactly when a figure counts less than the org's queue stats do.
  const filtered = condition !== undefined;

  // With nothing chosen, what the server bucketed by IS what auto resolved to, whatever the
  // client's own ladder says. With a width in force, the finest this window allows is the best
  // guess available.
  const autoPeriod =
    period === undefined && activity.data !== undefined
      ? activity.data.period
      : allowedPeriods[0];

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    writeStoredBoolean(expandedKey, next);
  };

  return (
    <section className={styles.panel} aria-label="Invocation activity">
      <div className={styles.header}>
        <div className={styles.headerLead}>
          <button
            type="button"
            className={styles.toggleButton}
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={toggleExpanded}
          >
            <ChevronRight size={16} aria-hidden="true" />
            Activity
          </button>
          {!expanded && (
            <ActivityFigureSummary
              figures={figures}
              loading={isPending}
              failed={isError}
              previous={isPlaceholder}
            />
          )}
        </div>
        <div className={styles.headerControls}>
          {/*
            The figures and chart below answer the previous request until the new one lands. They
            stay up, dimmed, rather than giving way to a spinner; this says which request they
            are for. A refresh of the same request shows nothing here.
          */}
          {isPlaceholder && (
            <span className={styles.updating} role="status">
              <Spinner size="sm" />
              Updating…
            </span>
          )}
          {onReset !== undefined && (
            <button
              type="button"
              className={styles.resetButton}
              onClick={resetView}
            >
              Reset view
            </button>
          )}
          {expanded && (
            <ToggleGroup<ActivityChartMode>
              aria-label="Chart"
              size="sm"
              value={[mode]}
              onValueChange={(groupValue) => {
                const next = groupValue.at(-1);
                if (next !== undefined) {
                  setMode(next);
                }
              }}
            >
              {ACTIVITY_CHART_MODES.map((option) => (
                <Toggle
                  key={option.value}
                  value={option.value}
                  className={styles.blueToggle}
                >
                  {option.label}
                </Toggle>
              ))}
            </ToggleGroup>
          )}
          <AutoRefreshButton on={autoRefresh} onChange={onAutoRefreshChange} />
        </div>
      </div>
      {expanded && (
        <div id={bodyId} className={styles.body}>
          {isPending && (
            // Every query's first load, not just the chart's: the figures and the chart arrive
            // together, so a figure on screen is never one of the four still in flight.
            <p className={styles.loading} role="status">
              <Spinner size="sm" />
              Loading activity for this window…
            </p>
          )}
          {!isPending && isError && (
            // A blank panel would read as "nothing happened", the opposite of what a failure means.
            <p role="alert" className={styles.placeholder}>
              {ACTIVITY_LOAD_ERROR}
            </p>
          )}
          {!isPending && !isError && activity.data !== undefined && (
            <div
              className={classNames(styles.content, {
                [styles.contentPrevious]: isPlaceholder,
              })}
              aria-busy={isPlaceholder}
            >
              {/*
                The table below the panel has no such limit, so without this a reader who filtered
                to an older span would see rows the chart and figures leave out.
              */}
              {resolved.truncated && (
                <p className={styles.notice}>
                  <Info size={14} aria-hidden="true" />
                  The chart and figures cover the last{" "}
                  {ACTIVITY_WINDOW_MAX_LABEL}. The table lists everything.
                </p>
              )}
              <ActivityFigureCards
                figures={figures}
                queueStats={queueStats}
                filtered={filtered}
              />

              <div className={styles.section}>
                <InvocationActivityChart
                  key={chartInstance}
                  buckets={buckets}
                  period={activity.data.period}
                  mode={mode}
                  concurrencyLimit={figures.concurrencyLimit}
                  statusFilter={statusFilter}
                  onSelectRange={onWindowChange}
                />
                <ActivityChartFooter
                  mode={mode}
                  period={activity.data.period}
                  autoPeriod={autoPeriod}
                  periodOptions={periodOptions}
                  selected={period ?? "auto"}
                  onSelect={setChosenPeriod}
                />
              </div>

              {/* A whole section for "nothing is waiting" is space the table wants back. */}
              {waitingGroups.length > 0 && (
                <WaitingBreakdown
                  groups={waitingGroups}
                  groupBy={groupBy}
                  onGroupByChange={setGroupBy}
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function allowedPeriodsOf(options: PeriodOption[]): ActivityPeriod[] {
  return options
    .filter((option) => option.allowed)
    .map((option) => option.period);
}

function readStoredBoolean(key: string): boolean | null {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? null : stored === "true";
  } catch {
    return null;
  }
}

function writeStoredBoolean(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Private mode or a full store: the panel just forgets between visits.
  }
}
